import { inject, Injectable } from '@angular/core';
import { DtModelFormatEnum } from '@app/pages/digital-twin/interfaces/dt-model-format.enum';
import { Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import {
	digitalTwinDetailsQuery,
	DigitalTwinModelResponseType,
} from '../graphql/digital-twin-details.query';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class DtModelsOpenerService {
	private readonly router = inject(Router);
	private readonly apollo = inject(Apollo);

	getDigitalTwinModelsDetails(
		buildingId: string
	): Observable<DigitalTwinModelResponseType[]> {
		return this.apollo
			.query({
				query: digitalTwinDetailsQuery,
				variables: {
					buildingsId: [buildingId],
				},
			})
			.pipe(map(result => result.data.buildings[0]?.digitalTwin.models ?? []));
	}

	getPrioritisedModels<
		T extends {
			isFavorite: boolean;
			isFragmentConverted?: boolean;
			format: DtModelFormatEnum;
		},
	>(models: T[]): T[] {
		const favoriteModels = models.filter(item => item.isFavorite);

		if (favoriteModels.length > 0) {
			return favoriteModels;
		}

		const formatPriority = [
			DtModelFormatEnum.IFC,
			DtModelFormatEnum.GLB,
			DtModelFormatEnum.GLTF,
			DtModelFormatEnum.PDF,
			DtModelFormatEnum.JPEG,
		];

		for (const format of formatPriority) {
			const model = models.find(
				item => item.format === format && item.isFragmentConverted
			);
			if (model) {
				return [model];
			}
		}

		return models.length > 0 ? [models[0]] : [];
	}

	openModelsIn3dViewer(
		models: number[] | { id: number }[],
		buildingId: string,
		options?: { [key: string]: any }
	): void {
		const modelsIds: number[] = models.map(model => {
			if (typeof model === 'number') {
				return model;
			}
			return model.id;
		});

		const dtQueryParams = {
			buildingId: buildingId,
			models: modelsIds.join(','),
		};

		if (options?.preselectedCmmsMarkers) {
			dtQueryParams['preselectedCmmsMarkers'] = JSON.stringify(
				options.preselectedCmmsMarkers
			);
		}

		if (options?.filters) {
			dtQueryParams['filters'] = options.filters;
		}

		this.router.navigate(['digital-twin', '3d-viewer'], {
			queryParams: dtQueryParams,
		});
	}
}
